import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "container flex h-full items-center justify-between" }
const _hoisted_2 = ["href"]
const _hoisted_3 = ["href"]
const _hoisted_4 = ["onClick"]
const _hoisted_5 = { class: "hidden lg:inline" }
const _hoisted_6 = ["onClick", "href"]
const _hoisted_7 = { class: "relative flex items-center justify-between whitespace-nowrap pb-2" }
const _hoisted_8 = /*#__PURE__*/_createElementVNode("span", { class: "absolute bottom-0 block h-px w-full bg-gray-100" }, null, -1)
const _hoisted_9 = ["href"]
const _hoisted_10 = ["href"]

import BizIcon from '@/components/base/BizIcon.vue';
import type { Ref } from 'vue';
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'CampaignHeader',
  props: {
    editMode: { type: Boolean },
    entryHeader: { type: Boolean },
    homeLink: null,
    downloadLink: { default: '' },
    contactLink: { default: '' },
    langNav: null,
    siteNav: { default: () => [] },
    langSwitcherLabel: null
  },
  setup(__props: any) {

const props = __props as { editMode: boolean, entryHeader: boolean, homeLink: string, downloadLink: string, contactLink: string, langNav: {
            active: boolean;
            label: string;
            link: string;
        }[], siteNav: {
            label: string;
            link: string;
            icon: string;
        }[], langSwitcherLabel: string }


const transparent = ref(true);
const isClosing = ref(false);
const isMobileOpen = ref(false);
const showNavi = ref(true);
const siteNavOpen = ref(false);
const lastScrollPos = ref(0);
const siteSwitcher: Ref<Element | null> = ref(null);

onMounted(() => {
    document.addEventListener('scroll', onScroll, { passive: false });
    if (document.scrollingElement.scrollTop > 0) {
        transparent.value = false;
        showNavi.value = false;
    }
});

onBeforeUnmount(() => {
    document.removeEventListener('scroll', onScroll);
});

// hide/show navbar when scrolling up/down
function onScroll(event: Event) {
    if (props.editMode) {
        return;
    }

    if (this.isOpen) {
        event.preventDefault();
        return;
    }
    const scrollPos = Math.max(document.scrollingElement.scrollTop, 0);
    transparent.value = scrollPos === 0;
    showNavi.value = scrollPos <= lastScrollPos.value;
    lastScrollPos.value = scrollPos;
    isClosing.value = false;
    // if we scroll down (navbar will be hidden) and the site nav is open, close it too
    if (siteNavOpen.value) {
        siteNavOpen.value = showNavi.value;
    }
}

function toggleMobile() {
    isMobileOpen.value = !isMobileOpen.value;
    document.body.classList.toggle('overflow-hidden', isMobileOpen.value);
}

function onSiteNavClick(link: string) {
    location.href = link;
}

async function toggleSiteNav() {
    siteNavOpen.value = !siteNavOpen.value;
    await nextTick();
    document.addEventListener('click', onOutsideClick);
}

function onOutsideClick(evt: Event) {
    if (!siteNavOpen.value) {
        return;
    }
    let targetElement = evt.target as Element;

    do {
        if (targetElement === siteSwitcher.value) {
            return;
        }
        targetElement = targetElement.parentNode as Element;
    } while (targetElement);
    siteNavOpen.value = false;
    document.removeEventListener('click', onOutsideClick);
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["left-0 top-0 z-40 h-16 w-full duration-1000 lg:h-18 lg:transition-all", [
            __props.editMode ? 'relative bg-gray-500' : 'fixed',
            transparent.value && !isMobileOpen.value ? ' text-white' : 'bg-white text-black',
            { 'delay-1000': isClosing.value },
            { '-translate-y-full': !showNavi.value && !__props.editMode }
        ]])
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("a", {
        href: __props.homeLink,
        class: "inline-flex h-16 items-center transition-colors lg:h-full"
      }, [
        _createVNode(BizIcon, {
          class: "h-4 w-full fill-current lg:h-5",
          name: "bizerba-logo"
        })
      ], 8, _hoisted_2),
      (__props.entryHeader)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: _normalizeClass(["absolute right-8 top-4 flex h-7 w-7 shrink-0 items-center justify-center lg:hidden", [transparent.value && !isMobileOpen.value ? 'text-white' : 'text-gray-500']]),
            onClick: toggleMobile
          }, [
            _createVNode(BizIcon, {
              class: "h-full w-full cursor-pointer fill-current",
              name: isMobileOpen.value ? 'close' : 'open-burger'
            }, null, 8, ["name"])
          ], 2))
        : _createCommentVNode("", true),
      _createElementVNode("div", {
        class: _normalizeClass(["w-full lg:h-auto lg:max-h-full lg:overflow-visible lg:pt-0 lg:text-current lg:transition-none", [
                    isMobileOpen.value ? 'max-h-screen' : 'max-h-0',
                    __props.entryHeader
                        ? 'absolute left-0 top-16 h-screen overflow-hidden font-light text-black transition-all lg:static lg:overflow-visible'
                        : 'static -mt-6 overflow-visible pt-0 lg:mt-0'
                ]])
      }, [
        _createElementVNode("div", {
          class: _normalizeClass([[__props.entryHeader ? 'lg:bg-transparent lg:pb-0' : 'bg-transparent pb-0'], "flex h-full flex-col bg-white pb-16 lg:block lg:h-auto"])
        }, [
          _createElementVNode("div", {
            class: _normalizeClass(["w-full flex-1 items-center justify-end lg:flex lg:space-x-14", { flex: !__props.entryHeader }])
          }, [
            ((!transparent.value || isMobileOpen.value) && __props.entryHeader)
              ? _renderSlot(_ctx.$slots, "default", { key: 0 })
              : _createCommentVNode("", true),
            _createElementVNode("div", {
              class: _normalizeClass(["flex lg:mt-0", { 'mt-8 px-8 lg:px-0': __props.entryHeader }])
            }, [
              (__props.langNav.length > 1)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    class: _normalizeClass(["ml-auto flex items-end space-x-2 self-end font-light text-gray-500 lg:ml-6 lg:h-full lg:items-center lg:self-center", [
                                    {
                                        'scrollbar-hide overflow-x-auto lg:w-32': __props.langNav.length > 4
                                    },
                                    __props.entryHeader && __props.langNav.length > 4 ? 'w-52' : 'pl-2 lg:mr-4'
                                ]])
                  }, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.langNav, (langItem, l) => {
                      return (_openBlock(), _createElementBlock(_Fragment, {
                        key: langItem.link
                      }, [
                        (langItem.active)
                          ? (_openBlock(), _createElementBlock("span", {
                              key: 0,
                              class: _normalizeClass({ 'text-white': transparent.value })
                            }, _toDisplayString(langItem.label), 3))
                          : (_openBlock(), _createElementBlock("a", {
                              key: 1,
                              href: langItem.link,
                              class: "text-gray-400"
                            }, _toDisplayString(langItem.label.replace(/_.*$/, '')), 9, _hoisted_3)),
                        (l < __props.langNav.length - 1)
                          ? (_openBlock(), _createElementBlock("span", {
                              key: 2,
                              class: _normalizeClass({ 'text-gray-100': transparent.value })
                            }, " | ", 2))
                          : _createCommentVNode("", true)
                      ], 64))
                    }), 128))
                  ], 2))
                : _createCommentVNode("", true),
              (__props.siteNav.length > 0)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 1,
                    class: "relative mt-2 flex w-full cursor-pointer items-center self-stretch lg:mt-0 lg:w-auto",
                    ref_key: "siteSwitcher",
                    ref: siteSwitcher,
                    onClick: _withModifiers(toggleSiteNav, ["prevent"])
                  }, [
                    _createVNode(BizIcon, {
                      class: "mr-3 h-4 w-4 fill-current",
                      name: "sites"
                    }),
                    _createElementVNode("span", _hoisted_5, _toDisplayString(__props.langSwitcherLabel), 1),
                    _createVNode(BizIcon, {
                      class: _normalizeClass(["ml-3 h-3 w-3 fill-current transition-transform", [siteNavOpen.value ? '-rotate-90' : 'rotate-90']]),
                      name: "chevron-right"
                    }, null, 8, ["class"]),
                    _createElementVNode("div", {
                      class: _normalizeClass(["dropdown-scrollbar absolute top-full mt-4 flex w-full min-w-fit flex-col overflow-y-auto bg-white transition-all lg:mt-2 lg:w-fit", [
                                        siteNavOpen.value ? 'max-h-56' : 'max-h-0',
                                        __props.entryHeader ? 'left-0 lg:left-auto lg:right-0' : 'right-0'
                                    ]])
                    }, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.siteNav, (site, l) => {
                        return (_openBlock(), _createElementBlock("a", {
                          class: _normalizeClass(["relative block min-w-fit pt-6 text-black transition-colors last:pb-6 hover:text-gray-400", [__props.entryHeader ? 'pr-8 lg:px-8' : 'px-8']]),
                          onClick: ($event: any) => (onSiteNavClick(site.link)),
                          key: `lang-${l}-${site.label}`,
                          href: site.link
                        }, [
                          _createElementVNode("span", _hoisted_7, [
                            _createTextVNode(_toDisplayString(site.label) + " ", 1),
                            _createVNode(BizIcon, {
                              name: site.icon,
                              class: "ml-16 h-4 w-4 flex-shrink-0 rounded-full"
                            }, null, 8, ["name"]),
                            _hoisted_8
                          ])
                        ], 10, _hoisted_6))
                      }), 128))
                    ], 2)
                  ], 8, _hoisted_4))
                : _createCommentVNode("", true)
            ], 2)
          ], 2),
          _createElementVNode("div", {
            class: _normalizeClass(["flex h-16 w-full", [__props.entryHeader ? 'lg:hidden' : 'hidden']])
          }, [
            (__props.contactLink)
              ? (_openBlock(), _createElementBlock("a", {
                  key: 0,
                  href: __props.contactLink,
                  onClick: toggleMobile,
                  class: "flex flex-1 items-center justify-center bg-gray-100 text-gray-500"
                }, [
                  _createVNode(BizIcon, {
                    name: "contact",
                    class: "h-5 w-5 fill-current"
                  })
                ], 8, _hoisted_9))
              : _createCommentVNode("", true),
            (__props.downloadLink)
              ? (_openBlock(), _createElementBlock("a", {
                  key: 1,
                  href: __props.downloadLink,
                  onClick: toggleMobile,
                  class: "flex flex-1 items-center justify-center bg-cyan text-white"
                }, [
                  _createVNode(BizIcon, {
                    name: "download",
                    class: "h-5 w-5 fill-current"
                  })
                ], 8, _hoisted_10))
              : _createCommentVNode("", true)
          ], 2)
        ], 2)
      ], 2)
    ]),
    (!__props.entryHeader)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(["container -mt-4 hidden w-full border-b border-gray-400", [transparent.value ? 'lg:block' : 'lg:hidden']])
        }, null, 2))
      : _createCommentVNode("", true)
  ], 2))
}
}

})